import React, { useCallback, useState, useRef } from 'react';
import { Mic, FileAudio, X, Play } from 'lucide-react';

interface AudioUploadProps {
  label?: string;
  description?: string;
  maxFiles?: number;
  onChange?: (files: File[]) => void;
}

export function AudioUpload({ label, description, maxFiles = 5, onChange }: AudioUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setIsDragging(true);
    else if (e.type === "dragleave") setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const validFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('audio/'));
      setFiles(prev => {
        const updated = [...prev, ...validFiles].slice(0, maxFiles);
        if (onChange) onChange(updated);
        return updated;
      });
    }
  }, [maxFiles, onChange]);

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (onChange) onChange(updated);
      return updated;
    });
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {(label || description) && (
        <div>
          {label && <label className="text-sm font-medium text-gray-900">{label}</label>}
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      )}
      
      <div 
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col justify-center items-center w-full py-6 px-4 transition-all bg-emerald-50/30 border-2 border-dashed rounded-xl cursor-pointer hover:border-emerald-400 focus:outline-none ${isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-200'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDragging ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-100/50 text-emerald-500'}`}>
            <Mic size={24} />
        </div>
        <span className="font-medium text-emerald-800 text-sm mb-1">
            Solte seus áudios aqui
        </span>
        <span className="text-xs text-emerald-600/70 text-center max-w-xs">Arraste os arquivos MP3, WAV ou de gravação de WhatsApp dos vendedores (Max {maxFiles})</span>
        
        <input 
            ref={inputRef}
            type="file" 
            className="hidden" 
            accept="audio/*" 
            multiple={maxFiles > 1} 
            onChange={(e) => {
                if (e.target.files) {
                    const validFiles = Array.from(e.target.files).filter(f => f.type.startsWith('audio/'));
                    setFiles(prev => {
                        const updated = [...prev, ...validFiles].slice(0, maxFiles);
                        if (onChange) onChange(updated);
                        return updated;
                    });
                }
            }} 
        />
      </div>

      {files.length > 0 && (
         <div className="mt-2 space-y-2">
             {files.map((file, i) => (
                 <div key={i} className="flex items-center gap-3 p-3 bg-white border border-emerald-100 rounded-lg shadow-sm">
                     <button type="button" className="w-10 h-10 flex-shrink-0 bg-emerald-50 hover:bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 transition-colors">
                        <Play size={18} className="ml-1" />
                     </button>
                     <div className="flex-1 min-w-0">
                         <div className="flex items-center gap-2">
                             <FileAudio size={14} className="text-emerald-400" />
                             <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>{file.name}</p>
                         </div>
                         <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
                             <div className="bg-emerald-400 h-full" style={{ width: '0%' }}></div>
                         </div>
                     </div>
                     <span className="text-xs text-gray-400 min-w-[50px] text-right font-mono">
                         {(file.size / 1024 / 1024).toFixed(2)} MB
                     </span>
                     <button type="button" onClick={() => removeFile(i)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                         <X size={16} />
                     </button>
                 </div>
             ))}
         </div>
      )}
    </div>
  );
}
